import { useEffect, useState } from "react";
import { fetchLivePlayers } from "@/lib/api";
import { Users, Server } from "lucide-react";

export default function LivePlayerGrid() {
  const [data, setData] = useState({ players: [], total: 0, online_servers: 0 });

  useEffect(() => {
    let isMounted = true;
    const load = () =>
      fetchLivePlayers()
        .then((d) => isMounted && setData(d))
        .catch(() => {});

    load();
    const interval = setInterval(load, 25000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const players = data.players || [];

  if (!players.length) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center backdrop-blur-sm">
          <Users className="w-8 h-8 text-white/20 mb-2" />
          <div className="text-white/60 text-sm font-medium">No players online right now</div>
          <p className="text-white/30 text-xs mt-1">Be the first to join!</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="live-player-grid" className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Header Info Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2.5 text-xs sm:text-sm text-white/70 bg-white/5 border border-white/10 px-3.5 py-1.5 rounded-full backdrop-blur-md">
          <Users size={15} className="text-[#FFBF00]" />
          <span className="font-mono">
            <span className="text-[#FFBF00] font-bold text-sm sm:text-base mr-1">
              {data.total}
            </span>
            <span className="text-white/50">
              {data.total === 1 ? "player" : "players"} online across {data.online_servers} servers
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] sm:text-xs uppercase tracking-widest text-white/40 font-mono">
            Live · 25s refresh
          </span>
        </div>
      </div>

      {/* Static Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {players.map((p, i) => (
          <PlayerCard key={`${p.username}-${i}`} player={p} />
        ))}
      </div>
    </div>
  );
}

function PlayerCard({ player }) {
  const [imgSrc, setImgSrc] = useState(
    player.avatar || `https://mc-heads.net/avatar/${player.username}/64`
  );

  return (
    <div
      className="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] backdrop-blur-md border border-white/10 w-full hover:border-[#FFBF00]/50 hover:bg-white/[0.07] transition-all duration-200 select-none cursor-pointer"
      style={{
        boxShadow: `0 4px 20px ${player.color ? player.color + "15" : "rgba(0,0,0,0.3)"}`,
      }}
    >
      <div className="relative shrink-0">
        <img
          src={imgSrc}
          alt={player.username}
          className="w-9 h-9 rounded-lg object-cover bg-black/40 border border-white/10"
          loading="lazy"
          onError={() => setImgSrc("https://mc-heads.net/avatar/steve/64")}
        />
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-black" />
      </div>
      <div className="leading-tight min-w-0 flex-1">
        <div className="text-xs sm:text-sm font-bold text-white/90 truncate group-hover:text-[#FFBF00] transition-colors">
          {player.username}
        </div>
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          <Server size={10} className="shrink-0 text-white/40" />
          <span
            className="text-[9px] sm:text-[10px] uppercase tracking-wider font-mono font-semibold truncate"
            style={{ color: player.color || "#888" }}
          >
            {player.server}
          </span>
        </div>
      </div>
    </div>
  );
}